import { store } from 'App';
import { CustomLoginPopupWindowName, OtherUserAuthenticatedError } from 'AuthConstants';
import { authlog } from 'AuthLogger';
import { IAuthResultMessage } from 'AuthService.types';
import { setAuthBreakpoint } from 'AuthServicerHelper';
import { pageDataService } from 'common';
import { shouldSetupMsalBroker } from 'common/utilities/FlightSetting';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { loginMfaPath } from 'Routes';
import { IAuthState, ILoginRequestState, onLoginRedirectSuccess } from 'store';

import {
  AuthenticationResult,
  EventType,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { PairwiseBrokerApplication } from '@azure/msal-browser-1p';

const msalLogLevel = LogLevel.Warning;

class AuthService {
  private msalInstance: IPublicClientApplication | undefined;

  private brokerInitialization: Promise<void> | undefined;

  // login popup created by loginPopup API call
  private msalLoginPopup: Window | null = null;

  // custom login popup window to start loginRedirect on a separate window
  // to work around the issue with iOS Safari & webview
  private customLoginPopup: Window | null = null;

  // handles known login errors when thrown from the separate window
  private handleLoginError: (error: unknown) => boolean = () => false;

  private msalConfig = {
    auth: {
      clientId: pageDataService.getAadClientId(),
      authority: 'https://login.microsoftonline.com/consumers',
      redirectUri: `${pageDataService.getConsumerSecurityPortalHost()}/signin-spa`,
      // disables the automatic redirection back to original url to avoid page load loop &
      // for faster redirection via client-side routing instead of full page reload
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'localStorage',
    },
    system: {
      logLevel: msalLogLevel,
      piiLoggingEnabled: false,
      loggerOptions: {
        loggerCallback: (logLevel: LogLevel, message: string, containsPii: boolean) => {
          if (containsPii || logLevel > msalLogLevel) {
            return;
          }
          authlog(`msal => [${logLevel}] ${message}`);
          TelemetryProvider.Instance.trackEvent('msalLog', { logLevel, message });
        },
      },
    },
  };

  constructor() {
    setAuthBreakpoint();
    if (pageDataService.hasActiveSubscription()) {
      this.setupPublicClientApplication();
    } else if (shouldSetupMsalBroker()) {
      this.setupExperimentalPublicClientApplication();
    }
  }

  public getExperimentalPublicClientApplication(): IPublicClientApplication {
    return this.msalInstance!;
  }

  public getPublicClientApplication(): IPublicClientApplication {
    return this.msalInstance!;
  }

  public async waitForBrokerInitialization(): Promise<void> {
    // Subscriber and broker-flight-off paths have no pairwise broker to await.
    await this.brokerInitialization;
  }

  public createCustomLoginPopup(handleError: (error: unknown) => boolean, useDefaultScope = false) {
    if (this.customLoginPopup) {
      this.customLoginPopup.focus();
      return;
    }

    const currentLocation = window.location.pathname + window.location.search;
    const baseUrl = pageDataService.getConsumerSecurityPortalHost();
    const useDefaultScopeParam = useDefaultScope ? '&useDefaultScope=true' : '';
    const targetUrl = `${baseUrl}${loginMfaPath}?originUrl=${encodeURIComponent(currentLocation)}${useDefaultScopeParam}`;

    const isMobile = window.innerWidth <= 768;

    const width = isMobile ? window.innerWidth : 500;
    const height = isMobile ? window.innerHeight : 500;
    const { screenX, screenY, outerWidth, outerHeight } = window;

    const left = screenX + Math.max(outerWidth - width, 0) / 2;
    const top = screenY + Math.max(outerHeight - height, 0) / 2;

    // sets handleLoginError from the callback provided by the parent window
    this.handleLoginError = handleError;

    // listen for the event from the custom login popup
    window.addEventListener('message', this.handleCustomLoginPopupMessage);

    // open a new window to start loginRedirect
    TelemetryProvider.Instance.trackEvent('createCustomLoginPopup', { currentLocation });
    this.customLoginPopup = window.open(
      targetUrl,
      CustomLoginPopupWindowName,
      `width=${width},height=${height},top=${top},left=${left},status=no,resizeable=yes,toolbar=no,menubar=no,scrollbars=yes`
    );

    // check if the custom login popup is closed every second
    if (this.customLoginPopup) {
      const checkPopupClosed = setInterval(() => {
        if (!this.customLoginPopup || (this.customLoginPopup && this.customLoginPopup.closed)) {
          authlog(`custom login popup closed`);
          clearInterval(checkPopupClosed);
          window.removeEventListener('message', this.handleCustomLoginPopupMessage);
          this.customLoginPopup = null;
        }
      }, 1000);
    }
  }

  public focusLoginPopupWindow() {
    if (this.msalLoginPopup) {
      this.msalLoginPopup.focus();
    }
  }

  public IsLoginPopupWindowOpen(): boolean {
    return this.msalLoginPopup !== null && !this.msalLoginPopup.closed;
  }

  private setupPublicClientApplication() {
    this.msalInstance = new PublicClientApplication(this.msalConfig);

    this.msalInstance.addEventCallback((event) => {
      authlog(`msal event => + ${event.eventType}`);

      if (event.eventType === EventType.POPUP_OPENED && event.payload) {
        this.msalLoginPopup = (event.payload as { popupWindow: Window }).popupWindow;
      }
    });

    this.initializeMsal();
  }

  private setupExperimentalPublicClientApplication() {
    const brokerApp = new PairwiseBrokerApplication(this.msalConfig, {
      actAsBroker: true,
      preferredInteractionType: InteractionType.Popup,
    });
    this.msalInstance = brokerApp;
    brokerApp.addEventCallback((event) => {
      authlog(`msal (pairwiseBrokerApp) event => + ${event.eventType}`);
    });

    // MSAL 5.6.1 initialize() also installs the pairwise broker message listener.
    this.brokerInitialization = brokerApp.initialize();
    this.initializeMsal();
  }

  private async initializeMsal() {
    try {
      // In MSAL v3+, initialize() must be called before handleRedirectPromise()
      await (this.brokerInitialization ?? this.msalInstance!.initialize());
      if (this.brokerInitialization) {
        TelemetryProvider.Instance.trackEvent('msalBrokerInitialized', {
          clientId: this.msalConfig.auth.clientId,
          origin: window.location.origin,
        });
      }
      this.trySetActiveAccount();

      this.msalInstance!.handleRedirectPromise()
        .then((authResult: AuthenticationResult | null) => {
          if (!authResult) {
            return;
          }

          authlog(`handleRedirect success => account: ${authResult.account?.username} state: ${authResult.state}`);
          this.trySetActiveAccount();

          if (authResult.state) {
            const requestState = JSON.parse(authResult.state) as ILoginRequestState;
            const isCurrentUserAuthenticated = this.isCurrentUserAuthenticated(authResult);
            TelemetryProvider.Instance.trackEvent('msalHandleRedirectSuccess', {
              ...requestState,
              isCurrentUserAuthenticated,
            });

            if (requestState.isCustomPopup) {
              const message: IAuthResultMessage = {
                success: true,
                isCurrentUserAuthenticated,
                originUrl: requestState.originUrl,
              };

              authlog(`posting a success message => originUrl: ${message.originUrl}`);

              // post a message to a main window to inform loginRedirect success
              window.opener?.postMessage(message, pageDataService.getConsumerSecurityPortalHost());
            } else if (isCurrentUserAuthenticated) {
              // loginRedirect was called from the current window (not from a custom popup)
              store.dispatch(onLoginRedirectSuccess({ requestState } as IAuthState));
            }
          }
        })
        .catch((error) => {
          authlog(`handleRedirect error => ${error}`);

          // window.name is set when the custom popup is opened in createCustomLoginPopup
          const isCustomPopup = window?.name === CustomLoginPopupWindowName;
          if (isCustomPopup) {
            const message: IAuthResultMessage = {
              success: false,
              isCurrentUserAuthenticated: false,
              error,
            };
            // post a message to a main window to inform loginRedirect error
            authlog('posting an error message');
            window.opener?.postMessage(message, pageDataService.getConsumerSecurityPortalHost());
          } else {
            TelemetryProvider.Instance.trackException({
              name: 'msalHandleRedirectFailed',
              message: error as string,
            });
            this.handleLoginError(error);
          }
        });
    } catch (error) {
      authlog(`initialize error => ${error}`);
      if (this.brokerInitialization) {
        TelemetryProvider.Instance.trackUnknownException(error, 'msalBrokerInitializeFailed');
        return;
      }
      TelemetryProvider.Instance.trackException({
        name: 'msalInitializeFailed',
        message: error as string,
      });
    }
  }

  private handleCustomLoginPopupMessage = (event: MessageEvent) => {
    let authResult: IAuthResultMessage | undefined;
    if (this.customLoginPopup && event.origin === window.location.origin && event.data) {
      authResult = event.data as IAuthResultMessage;
    }

    // authResult must be set
    if (!authResult) {
      return;
    }

    // Handle success state
    if (authResult.success && authResult.originUrl) {
      TelemetryProvider.Instance.trackEvent('loginSuccessFromCustomPopup', {
        originUrl: authResult.originUrl,
        isCurrentUserAuthenticated: authResult.isCurrentUserAuthenticated,
      });
      authlog(
        `EventListener: received auth success message => originUrl: ${authResult.originUrl}, ` +
          `isCurrentUserAuthenticated: ${authResult.isCurrentUserAuthenticated}`
      );
      // close the custom login popup
      this.customLoginPopup?.close();
      this.customLoginPopup = null;

      if (authResult.isCurrentUserAuthenticated) {
        store.dispatch(
          onLoginRedirectSuccess({
            requestState: { originUrl: authResult.originUrl, loginCount: 0, isCustomPopup: true },
          } as IAuthState)
        );
      } else {
        // User is authenticated with the wrong account (different account from the account currently signed into Defender)
        this.handleLoginError(OtherUserAuthenticatedError);
      }
    }

    // Handle error state
    if (authResult.success === false && !!authResult.error) {
      authlog(`EventListener: received auth error message`);
      TelemetryProvider.Instance.trackException({
        name: 'msalHandleRedirectFailed',
        message: authResult.error as string,
      });

      // calls handleLoginError and closes customLoginPopup if the error was handled
      const isErrorHandled = this.handleLoginError(authResult.error);
      if (isErrorHandled) {
        this.customLoginPopup?.close();
        this.customLoginPopup = null;
      }
    }
  };

  public trySetActiveAccount() {
    const app = this.msalInstance!;
    const accountCount = app.getAllAccounts().length;
    if (accountCount > 0) {
      const email = pageDataService.getUserData().userPrincipalName;
      const account = app.getAllAccounts().find((a) => localeIgnoreCaseEquals(a.username, email));
      let isActiveAccountSet = false;
      if (account) {
        // Set active account by the current user authenticated on the backend
        authlog(`set active account => ${account.username}`);
        app.setActiveAccount(account);
        isActiveAccountSet = true;
      }

      authlog(`active account: ${app.getActiveAccount()?.username}`);
      TelemetryProvider.Instance.trackEvent('msalTrySetActiveAccount', { accountCount, isActiveAccountSet });
    }
  }

  // eslint-disable-next-line class-methods-use-this
  public isCurrentUserAuthenticated(authResult: AuthenticationResult): boolean {
    const email = pageDataService.getUserData().userPrincipalName;
    return localeIgnoreCaseEquals(authResult.account?.username, email);
  }
}

export const authService = new AuthService();
